import {
  EvaluationEngineDeterminedResult,
  Institution,
  MatchIdentifiedBy,
  RulesetSnapshot,
  SearchStatuses,
  SnoozeData,
  TimePatientArmEvaluationState,
  TrialArm,
  TimePrescreeningMatchingQueueRequestDtoType,
  TimePrescreeningMatchingQueueResponseDtoType,
  TimePrescreeningPatientArmEvaluationsResponseDtoType,
  TimePrescreeningScreenedPatientsResponseDtoType,
  TimePatientQueryAnswerDetailDtoType as SharedTimePatientQueryAnswerDetailDtoType,
  TimePatientQueryAnswersResponseDtoType as SharedTimePatientQueryAnswersResponseDtoType,
  type PatientQueryFeedbackDetailDtoType,
} from '@tempus/trials-types';

export interface TimePrescreeningSearchStatusEntryDtoType {
  trialId: string;
  status: SearchStatuses;
  updatedAt: Date | string;
}

export interface TimePrescreeningSearchStatusResponseDtoType {
  latestSearch: TimePrescreeningSearchStatusEntryDtoType | Record<string, never>;
  latestSuccessfulSearch: TimePrescreeningSearchStatusEntryDtoType | Record<string, never>;
}

export const v3MatchingQueueStates = (
  ...states: TimePatientArmEvaluationState[]
): TimePrescreeningMatchingQueueRequestDtoType['states'] =>
  states as TimePrescreeningMatchingQueueRequestDtoType['states'];

export interface UnscreenedMatchRowDto {
  id: string;
  createdAt: string;
  updatedAt: string;
  currentState: TimePatientArmEvaluationState;
  currentStateDate: string;
  userId: string;
  timePatientId: string;
  armId: string;
  reportDate: string | null;
  latestEncounterDate: string | null;
  hasNewData: boolean;
  evaluationEngineDeterminedResult: EvaluationEngineDeterminedResult | Record<string, unknown> | null;
  metadata: UnscreenedMatchEvaluationMetadata;
  userInput: UnscreenedMatchUserInput;
  rulesetSnapshot: RulesetSnapshot[];
  timePatient: UnscreenedMatchTimePatientDto;
  patientRanking: UnscreenedMatchPatientRanking | null;
  timePatientVisit: UnscreenedMatchTimePatientVisit | null;
  arm?: TrialArm;
}

export interface UnscreenedMatchEvaluationMetadata {
  entrySource: string;
  tempusSequenced: boolean;
  orderingProviderId: string;
  rescreenedAt: string | null;
}

export interface UnscreenedMatchUserInput {
  identifiedBy?: MatchIdentifiedBy;
  recruitmentVendor?: string | null;
  biomarkers?: string | null;
  notes?: string | null;
  reasonNotAMatch?: string | null;
  snoozeData?: SnoozeData | SnoozeData[] | null;
}

export interface UnscreenedMatchTimePatientDto {
  id: string;
  patientLinkageId: string;
  institutionId: string;
  locationId?: string | null;
  status: string | null;
  billingId: string;
  physicianId: string | null;
  responseRequired: boolean;
  responseRequiredOnDate: string | null;
  responseRequiredOffDate: string | null;
  userId: string;
  location?: Pick<Institution, 'id' | 'name' | 'shortName'>;
  institution?: Pick<Institution, 'id' | 'name' | 'shortName'>;
}

export interface UnscreenedMatchPatientRanking {
  armId: string;
  timePatientId: string;
  score: number | null;
  hasFailure?: boolean; // an active prompt failed, so score is null
}

export interface UnscreenedMatchTimePatientVisit {
  visitDate: string | null;
  visitType: string | null;
}

export type TimePrescreeningMatchingQueueResponseWithRows = Omit<
  TimePrescreeningMatchingQueueResponseDtoType,
  'rows'
> & {
  rows: UnscreenedMatchRowDto[];
};

export type TimePrescreeningScreenedPatientsResponseWithRows = Omit<
  TimePrescreeningScreenedPatientsResponseDtoType,
  'rows'
> & {
  rows: UnscreenedMatchRowDto[];
};

export function getUnscreenedMatchEvalEngineIsMatch(
  evaluationEngineDeterminedResult: UnscreenedMatchRowDto['evaluationEngineDeterminedResult'],
): boolean | null {
  if (!evaluationEngineDeterminedResult || typeof evaluationEngineDeterminedResult !== 'object') {
    return null;
  }

  const { result } = evaluationEngineDeterminedResult as { result?: boolean };

  return typeof result === 'boolean' ? result : null;
}

export type PatientCohortMatchWireRow = {
  id: string;
  armId: string;
  createdAt: string;
  additionalDetails?: {
    internalRnToScreeningRnNote?: string | null;
  };
};

export type PatientPageArmEvaluationRow = UnscreenedMatchRowDto & {
  disableRescreen?: boolean;
  isRescreenClone?: boolean;
  isRescreenBase?: boolean;
};

export type PatientArmEvaluationsResponse = Omit<
  TimePrescreeningPatientArmEvaluationsResponseDtoType,
  'pending' | 'excluded' | 'screened' | 'pastScreenedArmEvaluations' | 'patientCohortMatches'
> & {
  pending: PatientPageArmEvaluationRow[];
  excluded: PatientPageArmEvaluationRow[];
  screened: PatientPageArmEvaluationRow[];
  pastScreenedArmEvaluations: UnscreenedMatchRowDto[];
  patientCohortMatches: PatientCohortMatchWireRow[];
};

export type TimePatientQueryAnswerDetailDtoType = Omit<
  SharedTimePatientQueryAnswerDetailDtoType,
  'updatedAt' | 'answerFeedback'
> & {
  updatedAt: string | Date;
  answerFeedback: (Omit<PatientQueryFeedbackDetailDtoType, 'updatedAt'> & { updatedAt: string | Date }) | null;
};

export interface TimePatientQueryAnswersResponseDtoType extends Omit<
  SharedTimePatientQueryAnswersResponseDtoType,
  'lastUpdated' | 'details'
> {
  lastUpdated: Date | string | null;
  details: TimePatientQueryAnswerDetailDtoType[];
}
